import {Component, ElementRef, ViewChild} from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
} from '@angular/router';
import {AuthService} from '@drl/core/auth/auth.service';
import {ComponentBase} from '@drl/core/component-base';
import {UserSessionStoreService as StoreService} from '@drl/core/store/user-session-store.service';
import {NbMenuItem, NbMenuService, NbSidebarService} from '@nebular/theme';
import {isEqual, keys} from 'lodash';
import {NgxPermissionsObject, NgxPermissionsService} from 'ngx-permissions';
import {distinctUntilChanged, filter} from 'rxjs/operators';

import {menuItem} from './pages-menu';
import {patientMenuItem} from './patient-pages-menu';
import {IpsTranslateService} from '@drl/core/localization/ips-translate.service';
import {AppoinmentService} from './care-central/appoinment.service';
import {environment} from '@drl/env/environment';
import {TranslateService} from '@ngx-translate/core';
import { SystemStoreFacadeService } from '@drl/core/store/system-store-facade.service';
import { ConfigKeys } from '@drl/core/enums';
import { FeatureConfigKeys, SubFeatureConfigKeys } from '@drl/core/enums/feature-config-keys.enum';
import { FeatureModel } from '@drl/core/auth/models/feature.model';

const patientsPath = '/main/patients';

@Component({
  selector: 'app-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss'],
})
export class MainComponent extends ComponentBase {
  @ViewChild('scrollBottom')
  private readonly scrollBottom: ElementRef;
  menuItems: NbMenuItem[] = menuItem(this.translateSvc.translate);
  patientMenuItems = patientMenuItem(this.translateSvc.translate);
  filteredMenuItems: NbMenuItem[] = [];
  patientMenuSet = false;
  isOnlineAccess: boolean;
  loading: boolean;
  leftMenuLoading: boolean;
  isVCEnabled = false;
  copyRightLbl: string;
  currentYear: number;
  featureConfigs: FeatureModel[];
  constructor(
    // sonarignore:start
    private readonly permissionsService: NgxPermissionsService,
    private readonly router: Router,
    private readonly store: StoreService,
    private readonly authService: AuthService,
    private readonly menu: NbMenuService,
    private readonly sidebarService: NbSidebarService,
    protected readonly translateSvc: IpsTranslateService, // sonarignore:end
    public readonly patientService: AppoinmentService,
    public readonly translate: TranslateService,
    private readonly systemStoreFacade: SystemStoreFacadeService,
  ) {
    super();
    const ipadStdSize = 1200;
    this.leftMenuLoading = true;
    this.systemStoreFacade.getFeatureConfigs().subscribe((fConfigs)=>{
      if(fConfigs && fConfigs.features){
        this.featureConfigs = fConfigs.features;
        this.setMenus();
      }
      this.leftMenuLoading = false;
    });
    menu.onSubmenuToggle().subscribe(event => {
        if (event.item.title === translate.instant('configurationsLeftMenuLbl')) {
        setTimeout(() => {
          if (this.scrollBottom && this.scrollBottom.nativeElement) {
            this.scrollBottom.nativeElement.scrollTop =
            this.scrollBottom.nativeElement.scrollTop +
            this.scrollBottom.nativeElement.scrollHeight;
          }
        }, Number('400'));
      }
    });
    menu.onItemClick().subscribe(() => {
      if (window.screen.availWidth <= ipadStdSize) {
        this.sidebarService.compact('menu-sidebar');
      }
    });

    router.events.subscribe(val => {
      if (
        'url' in val &&
        val.url &&
        val.url.includes(`${patientsPath}/`) &&
        !val.url.includes(`add-update`) &&
        !this.patientMenuSet
        ) {
          const appendUrl = this.getAppendUrl(val.url);
          this.menuItems = this.getAbsUrl(this.patientMenuItems, appendUrl);
          this.patientMenuSet = true;
        } else {
          this.menuItems = menuItem(this.translateSvc.translate);
          this.patientMenuSet = false;
          this.resetPatientMenu(this.patientMenuItems);
        }
        this.setMenus();
      });
    }

  checkOnlineAccess() {
    this.loading = true;
    const tenantName = {
      config_key: ConfigKeys.VcModuleConfig,
    };
    this.patientService.buttonDetails(tenantName).subscribe(
      data => {
        this.loading = false;
        this.isOnlineAccess = data.configValue[ConfigKeys.IsVcActive];
        this.setMenus();
      },
      err => {
        this.loading = false;
        this.setMenus();
      },
    );
  }

  getAppendUrl(url: string) {
    const result = url.replace(`${patientsPath}/`, '');
    let append: string;
    if (result.includes('/')) {
      append = result.substring(0, result.indexOf('/') + 1);
    } else {
      append = result;
    }

    return `${patientsPath}/${append}/`;
  }

  getAbsUrl(menu: NbMenuItem[], appendUrl: string): NbMenuItem[] {
    return menu.map(item => {
      const newItem: NbMenuItem = item;
      if (item.link === patientsPath) {
        newItem.link = item.link;
      } else {
        newItem.link = appendUrl + item.link;
      }
      return newItem;
    });
  }

  resetPatientMenu(menu: NbMenuItem[]) {
    return menu.map(item => {
      const newItem: NbMenuItem = item;
      if (item.link !== patientsPath) {
        newItem.link = item.link
          .replace(`${patientsPath}/`, '')
          .split('/')
          .pop();
      }
    });
  }

  ngOnInit() {
    super.ngOnInit();
    // sonarignore:start
    //    if (this.isVCEnabled) {
    // sonarignore:end
    this.checkOnlineAccess();
    // }
    this.copyRightLbl = this.translate.instant('Copyright');
    this.currentYear = new Date().getFullYear();
  }

  private setMenus() {
    this.permissionsService.permissions$
      .pipe(
        distinctUntilChanged(
          (
            currentValue: NgxPermissionsObject,
            previousValue: NgxPermissionsObject,
          ) => isEqual(keys(currentValue), keys(previousValue)),
        ),
      )
      .subscribe(() => {
        this._setHiddenMenus(this.menuItems);
        this.filteredMenuItems = this.menuItems;
      });

    this._subscriptions.push(
      this.router.events
        .pipe(filter(event => event instanceof RouterEvent))
        .subscribe(event => {
          if (event instanceof NavigationStart) {
            const authToken = this.store.getAccessToken();
            if (!authToken && event.url !== '/login') {
              this.authService.logout();
            }
            // this.loading = true;
            // Show loading indicator
          }
          if (event instanceof NavigationEnd) {
            // this.loading = false;
            // Hide loading indicator
          }
          if (event instanceof NavigationError) {
            // Hide loading indicator
            // this.loading = false;
            // Present error to user
          }
        }),
    );
  }

  private _setHiddenMenus(menuItems: NbMenuItem[]) {
    menuItems.forEach(async menuToHide => {
      await this._setHidden(menuToHide);
      if (menuToHide.children && menuToHide.children.length > 0) {
        this._setHiddenMenus(menuToHide.children);
      }
    });
  }

  private async _setHidden(mItem: NbMenuItem) {
    if(mItem.title === this.translate.instant('dietPlansTitle')){
      mItem.hidden = this._checkForFeatureConfigs(mItem, FeatureConfigKeys.Diet);
    }
    else{
      mItem.hidden =
        (mItem.title === this.translate.instant('onlineAppointment') &&
          !this.isOnlineAccess) ||
        (mItem.data &&
          mItem.data.permission &&
          mItem.data.permission !== '*' &&
          !(await this.permissionsService.hasPermission(mItem.data.permission)));
    }
  }

  private _checkForFeatureConfigs(mItem: NbMenuItem, featureKey: FeatureConfigKeys){
    if(featureKey === FeatureConfigKeys.Diet && this.featureConfigs){
        const dietObj = this.featureConfigs.find((fConfig) => {
          return fConfig.featureKey === FeatureConfigKeys.Diet
          });
        if(!dietObj || (dietObj && !dietObj.enabled))
          return true;

        const dietPlanObj = dietObj.subFeatures.find((fConfig) => {
          return fConfig.subFeatureKey === SubFeatureConfigKeys.DietPlan
          });
        if(dietPlanObj && dietPlanObj.enabled){
          return false;
        }
        return true;
      }
    return mItem.hidden;
  }
}